import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import contactData from "../data/contactData";

export default function ContactUs() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(
        "https://ma-laravel.ma-uae.com/api/contact-us",
        {
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
        }
      );

      if (response.status === 201) {
        Swal.fire({
          icon: "success",
          title: t("Success"),
          text: t("Form submitted successfully! We'll get back to you soon."),
          confirmButtonText: t("OK"),
          confirmButtonColor: "#3896BE",
        });
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          company: "",
          service: "",
          message: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("Error"),
          text: t("Failed to submit the form. Please try again."),
          confirmButtonText: t("OK"),
          confirmButtonColor: "#3896BE",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("Error"),
        text: t(
          "An error occurred while submitting the form. Please try again later."
        ),
        confirmButtonText: t("OK"),
        confirmButtonColor: "#3896BE",
      });
      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      <section className="contact-landing">
        <div className="container h-full relative z-10 text-white flex flex-col items-center justify-center gap-[6px]">
          <div className="text-[50px] font-bold">{t("Contact Us")}</div>
          <div className="w-100 text-[20px] w-1/2 text-center">
            {t(
              "Let's discuss how MA Partners can help your business achieve its financial goals"
            )}
          </div>
        </div>
      </section>

      <section className="relative py-[40px]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center gap-[8px] text-center mb-12">
            <h2 className="text-[35px] font-bold">
              {t("Get in Touch")}
            </h2>
            <p className="text-lg text-[#404040] mx-auto max-w-[800px]">
              {t(
                "Ready to take your business to the next level? Contact our expert team today for professional accounting and consulting services."
              )}
            </p>
          </div>
          <div className="flex flex-wrap justify-between gap-[8px]">
            {contactData.map((item) => (
              <div
                key={item.id}
                className="w-full sm:w-[48%] lg:w-[24%] flex flex-col items-center gap-[8px] text-center bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-[#3896BE] to-[#1A4558]">
                  <img
                    src={item.icon}
                    alt={`${t(item.title)} icon`}
                    className="w-[30px] h-[30px]"
                  />
                </div>
                <h3 className="text-xl font-semibold text-[#3896BE] mb-2">
                  {t(item.title)}
                </h3>
                {item.link ? (
                  <a
                    href={item.link}
                    className="text-lg text-gray-800 hover:text-[#3896BE] transition-colors"
                  >
                    {item.value}
                  </a>
                ) : (
                  <span className="text-lg text-gray-800">{item.value}</span>
                )}
                <p className="text-sm text-gray-500 mt-2">{t(item.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-[40px]">
        <div className="container">
          <div className="flex flex-col items-center justify-center gap-[8px] text-center mb-12">
            <h2 className="text-[35px] font-bold">
              {t("Get in Touch")}
            </h2>
            <p className="text-lg text-[#404040] mx-auto max-w-[800px]">
              {t(
                "Ready to take your business to the next level? Contact our expert team today for professional accounting and consulting services."
              )}
            </p>
          </div>
          <div className="shadow-[0_0_10px_#00000029] p-[30px] rounded-[8px] bg-white">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-[48%]">
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-medium text-[#404040] mb-1"
                  >
                    {t("Full Name")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={t("Enter your full name")}
                    required
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                  />
                </div>
                <div className="w-full md:w-[48%]">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#404040] mb-1"
                  >
                    {t("Email Address")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("Enter your email address")}
                    required
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-[48%]">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-[#404040] mb-1"
                  >
                    {t("Phone Number")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t("Enter your phone number")}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                  />
                </div>
                <div className="w-full md:w-[48%]">
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-[#404040] mb-1"
                  >
                    {t("Company Name")}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t("Enter your company name")}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="service"
                  className="block text-sm font-medium text-[#404040] mb-1"
                >
                  {t("Service of Interest")}
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                >
                  <option value="" disabled>
                    {t("Select a service")}
                  </option>
                  <option value="audit">{t("Audit Services")}</option>
                  <option value="tax">{t("Tax Advisory")}</option>
                  <option value="forensic">{t("Forensic Accounting")}</option>
                  <option value="icv">{t("ICV Certification")}</option>
                  <option value="business">{t("Business Advisory")}</option>
                  <option value="liquidation">
                    {t("Liquidation Services")}
                  </option>
                  <option value="other">{t("Other")}</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-[#404040] mb-1"
                >
                  {t("Message")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t("Enter your message")}
                  required
                  rows="5"
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3896BE]"
                ></textarea>
              </div>
              <div className="text-right">
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 font-bold bg-[#3896BE] text-white px-6 py-3 rounded-md hover:bg-[#1A4558] transition-colors"
                >
                  <div>{t("Send")}</div>
                  <img
                    className="w-[20px] h-[20px]"
                    src={require("../../assets/icons/tabler_send.svg").default}
                    alt="Send icon"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="relative py-[40px]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center gap-[8px] text-center mb-12">
            <h2 className="text-[35px] font-bold">
              {t("Our Location")}
            </h2>
            <p className="text-lg text-[#404040] mx-auto max-w-[800px]">
              {t(
                "Visit our office in Abu Dhabi, UAE, or find us on the map below."
              )}
            </p>
          </div>
          <div className="shadow-[0_0_10px_#00000029] rounded-[8px] overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3631.3838773889374!2d54.376391385004716!3d24.472152684238605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDI4JzE5LjgiTiA1NMKwMjInMjcuMSJF!5e0!3m2!1sar!2sjo!4v1756056968815!5m2!1sar!2sjo"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t("Office Location Map")}
            ></iframe>
          </div>
          <div className="w-fit my-[20px] mx-auto">
            <Link
              to="https://maps.app.goo.gl/LPnf9sd1ypMykFSq7"
              className="flex items-center justify-center gap-2 font-bold bg-[#3896BE] text-white px-6 py-3 rounded-md hover:bg-[#1A4558] transition-colors"
            >
              <div>{t("Go To Location")}</div>
              <img
                className="w-[20px] h-[20px]"
                src={require("../../assets/icons/tabler_send.svg").default}
                alt="Send icon"
              />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}